import { useState } from "react";
import { Alert, Input, Modal, Table, message } from "antd";
import { useNavigate } from "react-router-dom";
import { QtyInput } from "./QtyInput";
import { useApi } from "../helpers/useApi";
import type { MRN, MRNSourceType } from "../api-models";

export interface ReceivableLine {
  sourceLineId: number;
  itemCode: string;
  itemName: string;
  unit?: string;
  outstanding: number;
}

interface ReceiveDialogProps {
  open: boolean;
  onClose: () => void;
  sourceType: MRNSourceType;
  sourceId: number;
  lines: ReceivableLine[];
  requireGDN?: boolean;
}

interface Split {
  acceptedQty: number;
  damagedQty: number;
  rejectedQty: number;
  remarks: string;
}

// Everything dispatched is accepted by default; the receiver reduces it only where
// reality differs.
function defaultSplits(rows: ReceivableLine[]): Record<number, Split> {
  return Object.fromEntries(
    rows.map((l) => [l.sourceLineId, { acceptedQty: l.outstanding, damagedQty: 0, rejectedQty: 0, remarks: "" }]),
  );
}

// The receipt half of a dispatch (spec §12). Accepted/damaged/rejected are recorded
// separately because they go to different places — and none of them ever edits the
// dispatch document's own quantities (spec §31.4).
export function ReceiveDialog({ open, onClose, sourceType, sourceId, lines, requireGDN }: ReceiveDialogProps) {
  const { request, loading } = useApi();
  const navigate = useNavigate();
  const [gdn, setGdn] = useState("");
  const [notes, setNotes] = useState("");
  const [splits, setSplits] = useState<Record<number, Split>>(() => defaultSplits(lines));

  function patch(id: number, next: Partial<Split>) {
    setSplits((prev) => ({ ...prev, [id]: { ...prev[id], ...next } }));
  }

  async function post() {
    if (requireGDN && !gdn.trim()) {
      message.error("The supplier's delivery note (GDN) number is required.");
      return;
    }
    const items = lines
      .map((l) => ({ sourceLineId: l.sourceLineId, ...splits[l.sourceLineId] }))
      .filter((it) => it.acceptedQty > 0 || it.damagedQty > 0 || it.rejectedQty > 0);
    if (items.length === 0) {
      message.error("Enter a received quantity for at least one line.");
      return;
    }
    const created = await request<MRN>({
      method: "POST",
      url: "/mrns",
      data: { sourceType, sourceId, supplierGdnNo: gdn, notes, items },
    });
    message.success("Receipt posted.");
    onClose();
    navigate(`/receipts/${created.id}`);
  }

  const short = lines.some((l) => {
    const s = splits[l.sourceLineId];
    return s && s.acceptedQty + s.damagedQty + s.rejectedQty < l.outstanding - 0.0000001;
  });

  return (
    <Modal title="Receive" open={open} onCancel={onClose} onOk={post} confirmLoading={loading} width={780} destroyOnHidden>
      {short && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This will open a discrepancy"
          description="Less is being accounted for than was dispatched, so the shortfall stays visible in transit until it is explained."
        />
      )}
      <Table
        size="small"
        rowKey="sourceLineId"
        dataSource={lines}
        pagination={false}
        columns={[
          { title: "Code", dataIndex: "itemCode" },
          { title: "Item", dataIndex: "itemName" },
          { title: "Dispatched", render: (_, r) => `${r.outstanding} ${r.unit ?? ""}` },
          {
            title: "Accepted",
            render: (_, r) => (
              <QtyInput value={splits[r.sourceLineId]?.acceptedQty} onChange={(v) => patch(r.sourceLineId, { acceptedQty: v ?? 0 })} />
            ),
          },
          {
            title: "Damaged",
            render: (_, r) => (
              <QtyInput value={splits[r.sourceLineId]?.damagedQty} onChange={(v) => patch(r.sourceLineId, { damagedQty: v ?? 0 })} />
            ),
          },
          {
            title: "Rejected",
            render: (_, r) => (
              <QtyInput value={splits[r.sourceLineId]?.rejectedQty} onChange={(v) => patch(r.sourceLineId, { rejectedQty: v ?? 0 })} />
            ),
          },
        ]}
      />
      {requireGDN && (
        <Input
          style={{ marginTop: 12 }}
          placeholder="Supplier delivery note (GDN) number"
          value={gdn}
          onChange={(e) => setGdn(e.target.value)}
        />
      )}
      <Input.TextArea style={{ marginTop: 12 }} rows={2} placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div style={{ marginTop: 8 }}>
        <small>Damaged goes to the destination&apos;s quarantine, outside Available stock. Rejected heads back toward the source.</small>
      </div>
    </Modal>
  );
}
